import { RasterAttributes } from "models/model";
import { GeoTiffRenderingMode, ShapefileFileType } from "models/projects";

import * as mapLayers from "./../../../../../constants/mapLayers.constants";

export type MapType = "map";
export type ProjectShapefileType = "projectShapefile";
export type OrgShapefileType = "orgShapefile";
export type McdanShapefileType = "mcdanShapefile";
export type PolygonBinType = "polygonBin";

export enum SHAPEFILE_FOLDER_TYPE {
  ORGANIZATION = "organization",
  SYSTEM = "system"
}

export enum SHAPEFILE_SOURCE {
  MAP = "map",
  PROJECT = "project",
  ORG = "org",
  MCDAN = "mcdan"
}

export enum TREE_NODE_TYPE {
  ROOT_FOLDER = "rootFolder",
  ROOT = "root",
  FOLDER = "folder",
  NODE = "node",
  SHAPEFILE = "shapefile",
  LAYER = "layer"
}

export type ShapefileSourceType = SHAPEFILE_SOURCE;

// Constants for shapefile tree section names
export enum SHAPEFILE_TREE_SECTIONS {
  PROJECT = "Project Shapefiles & GeoTIFFs",
  ORGANIZATION = "Organization Shapefiles & GeoTIFFs",
  MCDANIEL = "EVA Research Shapefiles & GeoTIFFs" // needs to be the same as EVA_RESEARCH_NAME in app.constants.ts
}

// Exported constants for easy access
export const PROJECT_SHAPEFILES_LABEL = SHAPEFILE_TREE_SECTIONS.PROJECT;
export const ORGANIZATION_SHAPEFILES_LABEL = SHAPEFILE_TREE_SECTIONS.ORGANIZATION;
export const MCDANIEL_SHAPEFILES_LABEL = SHAPEFILE_TREE_SECTIONS.MCDANIEL;

export const GRID_LAYERS_SECTION = "Grid Layers";

export interface GridLayerFolderT {
  title: string;
  layers: string[];
}

/**
 * Grid layers are grouped by region under the "Grid Layers" section.
 * The United States and Texas folders list every label layer first, then every
 * line layer, each group running smallest area to largest. The order is also
 * the draw order, so the labels stay on top of the lines. Canada keeps its
 * original order.
 */
export const GRID_LAYER_FOLDERS: GridLayerFolderT[] = [
  {
    title: "Canada",
    layers: [
      mapLayers.LSD_LABEL_LAYER,
      mapLayers.LSD_LAYER,
      "Section",
      "Township",
      "Section Label",
      // "Township Label"
      mapLayers.NTS_BLOCK_LABEL_LAYER
    ]
  },
  {
    title: "United States",
    layers: [
      mapLayers.US_SECTION_LABEL_LAYER,
      mapLayers.US_SECTION_LAYER,
      mapLayers.US_TOWNSHIP_LABEL_LAYER,
      mapLayers.US_TOWNSHIP_LAYER,
      mapLayers.COUNTY_LABEL_LAYER,
      mapLayers.COUNTY_LAYER
    ]
  },
  {
    title: "Texas Surveys",
    layers: [
      mapLayers.TX_ABSTRACT_LABEL_LAYER,
      mapLayers.TX_ABSTRACT_LAYER,
      mapLayers.TX_SECTION_LABEL_LAYER,
      mapLayers.TX_SECTION_LAYER,
      mapLayers.TX_BLOCK_LABEL_LAYER,
      mapLayers.TX_BLOCK_LAYER
    ]
  }
];

/**
 * Generates the project tree root map dynamically based on IPDB layer loaded status
 * @param ipdbGeoModelLoaded - Whether the IPDB Geo Model layer is loaded
 * @param ipdbControlWellsLoaded - Whether the IPDB Control Wells layer is loaded
 * @param rigFracEnabled - Whether the rig/frac beta feature is enabled
 * @returns Project tree root map with conditionally included IPDB and rig/frac layers
 */
export const getProjectTreeRootMap = (
  ipdbGeoModelLoaded: boolean,
  ipdbControlWellsLoaded: boolean,
  rigFracEnabled = false
) => {
  // Build Geo Model Layers array conditionally
  const geoModelLayers: string[] = [];
  if (ipdbGeoModelLoaded) {
    geoModelLayers.push(mapLayers.IPDB_LAYER);
  }
  if (ipdbControlWellsLoaded) {
    geoModelLayers.push(mapLayers.IPDB_CONTROL_WELLS_LAYER);
  }

  // Only include Geo Model Layers folder if at least one IPDB layer is loaded
  const hasAnyIpdbLayer = ipdbGeoModelLoaded || ipdbControlWellsLoaded;

  const baseMap = {
    "Well Layers": [
      mapLayers.WELL_LABEL_LAYER,
      mapLayers.WELL_LAYER,
      mapLayers.SELECTED_WELL_LAYER,
      mapLayers.TYPE_LOG_LAYER,
      // HIGHLIGHTED_WELL_LAYER,
      mapLayers.XDA_INTERCEPT_LAYER,
      mapLayers.XDA_LABEL,
      mapLayers.XDA_WELL_INTERCEPT_POINT
    ],
    [mapLayers.FACILITY_LAYER_ROOT]: [
      mapLayers.FACILITY_LAYER,
      mapLayers.FACILITY_LABEL_LAYER
    ],
    "Completion Layers": [
      mapLayers.COMPLETION_FRAC_LAYER,
      mapLayers.COMPLETION_PERF_LAYER
    ],
    ...(rigFracEnabled
      ? { [mapLayers.RIG_FRAC_LAYER_ROOT]: [mapLayers.RIG_LAYER, mapLayers.FRAC_LAYER] }
      : {}),
    // Flat list of every grid layer. The tree nests these under the region
    // folders, see GRID_LAYER_FOLDERS.
    [GRID_LAYERS_SECTION]: GRID_LAYER_FOLDERS.flatMap((folder) => folder.layers),
    [mapLayers.BASE_WELL_LAYER_PARENT]: [mapLayers.BASE_WELL_LAYER],
    [mapLayers.POLYGON_BINS_LAYERS]: [
      mapLayers.POLYGON_BINS_MY_FIELDS_LAYERS,
      mapLayers.POLYGON_BINS_ORG_FIELDS_LAYERS,
      mapLayers.POLYGON_BINS_PROJECT_FIELDS_LAYERS
    ],
    "Shapefile & GeoTIFF Label Layers": [
      mapLayers.SHAPEFILE_DETAILS_LABEL_LAYER,
      mapLayers.SHAPEFILE_NAME_LABELS_LAYER
    ],
    [SHAPEFILE_TREE_SECTIONS.PROJECT]: [],
    [SHAPEFILE_TREE_SECTIONS.ORGANIZATION]: [],
    [SHAPEFILE_TREE_SECTIONS.MCDANIEL]: []
  };

  // Conditionally add Geo Model Layers folder only if at least one IPDB layer is loaded
  if (hasAnyIpdbLayer) {
    return {
      ...baseMap,
      [mapLayers.GEO_MODEL_LAYERS]: geoModelLayers
    };
  }

  return baseMap;
};

export const projectTreeRootMap = getProjectTreeRootMap(false, false);

/**
 * Some inconsistencies in the type names, likely from disconnect of project tree, and system/org tree implementation.
 * rootFolder === root
 * folder === node
 */
type TreeNodeTypeT = "rootFolder" | "root" | "folder" | "node" | "shapefile" | "layer";

export interface ProjectTreeNodeT {
  type: TreeNodeTypeT;
  key: string;
  title: string;
  checked: boolean;
  folder?: ProjectShapefileFolderT;
  layer?: ProjectLayerT;
  children?: ProjectTreeNodeT[];
  source?: string;
  className?: string;
  shapefileId?: string;
  shapefileNodeId?: string;
  projectShapefileId?: string;
  projectId?: string;
  name?: string;
  properties?: string[];
  fieldSource?: string;
  originalIndex?: number;
  fileType?: ShapefileFileType;
  rasterAttributes?: RasterAttributes;
  color?: string;
  fillColor?: string;
  strokeColor?: string;
  thickness?: number;
  opacity?: number;
  order?: number;
  tilesStatus?:
    | "pending"
    | "processing"
    | "processing_high_zoom"
    | "completed"
    | "failed"
    | null;
  tilesReady?: boolean | null;
  // name is specific to antd tree
  disableCheckbox?: boolean;
  tooltip?: string;
  renderingMode?: GeoTiffRenderingMode;
}

export interface ProjectShapefileFolderT {
  source: ShapefileSourceType;
}

export interface ProjectLayerT {
  name: string;
  type: LayerType;
  selected: boolean;
  style?: ProjectLayerStyleT;
  zoom: [number, number];
  shapefileId?: string;
}

export type LayerType =
  | MapType
  | ProjectShapefileType
  | OrgShapefileType
  | McdanShapefileType
  | PolygonBinType;

export interface ProjectLayerStyleT {
  color: string;
  strokeColor: string;
  thickness: string | number;
  lineStyle?: string;
  fillPattern?: string;
  opacity: number;
}
